import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError'
import { API_ERROR_MESSAGES } from '@/constants/errorMessages'
import { useErrorToast } from '@/composables/useErrorToast'
import { useAppVersionCheck } from '@/hooks/useAppVersionCheck'
import { isChunkLoadError } from '@/utils/chunkLoadError'

/** Janela de dedupe: a mesma mensagem só gera toast 1x a cada 5s. */
export const ERROR_NOTIFY_WINDOW_MS = 5000

/** Mesma assinatura de `notifyError` do `useErrorToast`. */
export type NotifyError = (error: unknown, fallback?: string) => void

export interface GlobalErrorHandler {
  /** True se a mensagem ainda não foi notificada dentro da janela de 5s. */
  shouldNotify(message: string, now: number): boolean
  /** Loga SEMPRE no console; toasta (com dedupe) exceto para 401. */
  handleGlobalError(error: unknown): void
  /** Introspecção para testes: quantas mensagens estão rastreadas no mapa. */
  getTrackedMessageCount(): number
}

/**
 * Rede de segurança para erros não tratados (errorHandler do Vue +
 * unhandledrejection). Lógica pura: a notificação (`notify`) e o relógio
 * (`now`) são injetados para os testes não dependerem do toast real.
 *
 * Falha de chunk lazy pode ser deploy novo com a aba aberta: `isNewVersion`
 * confere o /version.json (e liga o aviso de versão nova). Só se não for, o
 * erro segue para o toast genérico — aí foi a rede.
 */
export function createGlobalErrorHandler(
  notify: NotifyError,
  now: () => number = Date.now,
  isNewVersion: () => Promise<boolean> = async () => false
): GlobalErrorHandler {
  const lastNotifiedAt = new Map<string, number>()

  function shouldNotify(message: string, nowMs: number): boolean {
    // Limpa entradas fora da janela para o mapa não crescer sem limite.
    for (const [trackedMessage, notifiedAt] of lastNotifiedAt) {
      if (nowMs - notifiedAt >= ERROR_NOTIFY_WINDOW_MS) {
        lastNotifiedAt.delete(trackedMessage)
      }
    }

    if (lastNotifiedAt.has(message)) return false

    lastNotifiedAt.set(message, nowMs)
    return true
  }

  function handleGlobalError(error: unknown): void {
    // Sempre loga o erro original para preservar stack e facilitar debug.
    console.error(error)

    if (isChunkLoadError(error)) {
      void isNewVersion().then((newVersion) => {
        if (!newVersion) notifyWithDedupe(error)
      })
      return
    }

    // 401 já é tratado pelo interceptor (limpa sessão e redireciona).
    if (getApiErrorStatus(error) === 401) return

    notifyWithDedupe(error)
  }

  function notifyWithDedupe(error: unknown): void {
    const message = getApiErrorMessage(error, API_ERROR_MESSAGES.GENERIC_ERROR)
    if (shouldNotify(message, now())) {
      notify(error, API_ERROR_MESSAGES.GENERIC_ERROR)
    }
  }

  return {
    shouldNotify,
    handleGlobalError,
    getTrackedMessageCount: () => lastNotifiedAt.size
  }
}

// ---------------------------------------------------------------------------
// Singleton usado pelo app real (criado lazy, depois do `app.use(Toast, ...)`)
// ---------------------------------------------------------------------------

let appHandler: GlobalErrorHandler | undefined

function getAppHandler(): GlobalErrorHandler {
  if (!appHandler) {
    const { notifyError } = useErrorToast()
    const { confirmUpdateAvailable } = useAppVersionCheck()
    appHandler = createGlobalErrorHandler(notifyError, Date.now, confirmUpdateAvailable)
  }
  return appHandler
}

/** Entrada única para `app.config.errorHandler` e `unhandledrejection`. */
export function handleGlobalError(error: unknown): void {
  getAppHandler().handleGlobalError(error)
}

let rejectionListenerInstalled = false

/**
 * Registra o listener de `unhandledrejection` UMA única vez — a factory do
 * app pode ser chamada mais de uma vez (ex.: testes), o listener não.
 */
export function installUnhandledRejectionListener(): void {
  if (rejectionListenerInstalled) return
  rejectionListenerInstalled = true

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    handleGlobalError(event.reason)
  })
}
